'use client';

import { useEffect } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';
import { useNonInitialEffect } from 'src/hooks/use-non-initial-effect';
import { useRecipeListApi } from 'src/hooks/recipe/list/use-recipe-list-api';
import { useRecipeBrowseFilters, useRecipeBrowsePagination, useRecipeBrowseSort } from 'src/hooks/recipe/browse';

import { getAvailableFilterBuckets } from 'src/models/recipe/list/filter-data';
import { FILTERS_CONFIG } from 'src/models/recipe/list/filters-config';
import { SORT_OPTIONS, getPreparedSortData } from 'src/models/recipe/list/sort-config';

import { type RecipeSearchOutput } from 'src/types';

import Iconify from 'src/components/iconify';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import RecipeList from './recipe-list';
import RecipeSort from './recipe-sort';
import RecipeSearch from './recipe-search';
import RecipeFilters from './recipe-filters';
import RecipeAppliedFilters from './recipe-applied-filters';
import RecipePagination from './recipe-pagination';

// ----------------------------------------------------------------------

type Props = {
  defaultRecipeList: RecipeSearchOutput | null;
  defaultFilterFields: Record<string, unknown>;
};

export default function RecipeBrowse({ defaultRecipeList, defaultFilterFields }: Props) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openFilters = useBoolean(false) as any;

  const { sortBy, handleSortBy } = useRecipeBrowseSort();
  const { page, pageSize, handlePage, handlePageSize, resetPage, clampPage } = useRecipeBrowsePagination();
  const { filters, bucketFilters, canReset, handleFilters, handleRemoveFilter, handleBucketSearch, handleResetFilters, handleSearch } = useRecipeBrowseFilters(defaultFilterFields, { onPageReset: resetPage });

  const { recipeList, loadRecipeList } = useRecipeListApi(defaultRecipeList);

  const availableFilterBuckets = getAvailableFilterBuckets(recipeList?.aggregations);
  const pageCount = Math.ceil((recipeList?.totalResults ?? 0) / pageSize);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (useNonInitialEffect as any)(() => {
    loadRecipeList({
      size: pageSize,
      from: (page - 1) * pageSize,
      sort: getPreparedSortData(sortBy),
      filters: filters.getDataForQuery(),
      bucketFilters: bucketFilters.getDataForBucketQuery(),
    });
    window.history.pushState({}, '', paths.recipe.browseWithFilters(sortBy, filters, pageSize, page));
  }, [filters, bucketFilters, sortBy, pageSize, page]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (useNonInitialEffect as any)(() => {
    resetPage();
  }, [sortBy]);

  // fallback: jeśli po załadowaniu danych aktualna strona > pageCount, cofnij do ostatniej dostępnej
  // odpala się też przy wejściu z linka (SSR zwraca recipeList od razu)
  useEffect(() => {
    clampPage(pageCount);
  }, [recipeList]);

  useEffect(() => {
    const handlePopState = () => { window.location.reload(); };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <Container>
      <CustomBreadcrumbs
        heading="Przepisy"
        links={[
          { name: 'KuliGuli', href: paths.homepage },
          { name: 'Przepisy' },
        ]}
        action={
          <Button
            component={RouterLink}
            href={paths.recipe.add}
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            Dodaj przepis
          </Button>
        }
        sx={{ mb: 4 }}
      />

      <Stack spacing={2.5}>
        <Stack
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-end', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' } as const}
        >
          <RecipeSearch
            query={filters.getFilterField('search') as string | null}
            onSearch={handleSearch}
          />

          <Stack direction="row" spacing={1} flexShrink={0}>
            <RecipeFilters
              open={openFilters.value}
              onOpen={openFilters.onTrue}
              onClose={openFilters.onFalse}
              items={FILTERS_CONFIG.map((config) => ({
                config,
                value: filters.getFilterField(config.code),
                options: availableFilterBuckets[config.code] ?? [],
              }))}
              onFilters={handleFilters}
              handleBucketSearch={handleBucketSearch}
              canReset={canReset}
              onResetFilters={handleResetFilters}
            />

            <RecipeSort sort={sortBy} onSort={handleSortBy} sortOptions={SORT_OPTIONS} />
          </Stack>
        </Stack>

        <Typography variant="body2">{`Znaleziono ${recipeList?.totalResults} rzeczy`}</Typography>
      </Stack>

      <RecipeAppliedFilters filters={filters} handleRemoveFilter={handleRemoveFilter} />

      <RecipeList
        recipes={recipeList?.items ?? []}
        sx={{ mt: { xs: 3, md: 5 } }}
      />

      <RecipePagination page={page} onPage={handlePage} pageSize={pageSize} onPageSize={handlePageSize} pageCount={pageCount} />
    </Container>
  );
}
